
import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthService } from './services/auth.service';
// FIX: Corrected import path for AppUser model to point to the barrel file.
import { AppUser } from './models/index';

export const authGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authService = inject(AuthService);
  const router: Router = inject(Router);

  const isLoggedIn = authService.isLoggedIn();
  const currentUser = authService.currentUser();
  const requiredFeature = route.data['feature'] as string | undefined;
  const allowedRoles = route.data['roles'] as AppUser['role'][] | undefined;

  // Generic check for login status
  if (!isLoggedIn) {
    const targetUrl = state.url.startsWith('/admin') || state.url.startsWith('/platform') ? '/login' : '/';
    router.navigate([targetUrl]);
    return false;
  }

  // --- CRITICAL FIX: Platform Admin "God Mode" ---
  // If the user is a platform_admin, they are allowed to go anywhere.
  if (currentUser?.role === 'platform_admin') {
      return true; 
  }

  // --- Prevent Normal Users from Accessing /platform ---
  // Because the check above returned true for platform_admin, anyone
  // reaching this point is NOT a platform admin.
  if (state.url.startsWith('/platform')) {
      router.navigate(['/admin/dashboard']);
      return false;
  }

  // --- Admin Panel Specific Logic (For Normal Users) ---
  if (state.url.startsWith('/admin')) {
    // 1. Allow all valid employee roles to access the main layout.
    // This allows Cashiers/Staff to access the Customer Onboarding page.
    const validEmployeeRoles: AppUser['role'][] = ['admin', 'owner', 'cashier', 'staff', 'doctor', 'accountant'];
    
    if (!currentUser || !validEmployeeRoles.includes(currentUser.role)) {
      authService.logout(); // Log out unauthorized users (e.g. if we had a pure 'customer' role trying to access admin)
      router.navigate(['/login'], { queryParams: { error: 'unauthorized' } });
      return false;
    }

    // 2. Check for specific Role Requirements (Layer 2 Security)
    if (allowedRoles) {
      if (!allowedRoles.includes(currentUser.role)) {
        // User has the right feature plan, but wrong role (e.g. Cashier trying to see Doctor Portal)
        if (state.url !== '/admin/customers') {
             router.navigate(['/admin/customers']);
        }
        return false;
      }
    }
    
    // 3. Check for specific Feature Permissions (Layer 1 Security - Plan based)
    if (requiredFeature) {
      if (authService.hasFeature(requiredFeature)()) {
        return true; // Has permission, allow access
      } else {
        // No permission for this specific feature (e.g. Reports).
        // Redirect to a safe default page.
        // For staff/cashiers who might not have dashboard access, redirect to customers (Onboarding).
        if (state.url !== '/admin/customers') {
             router.navigate(['/admin/customers']);
        }
        return false;
      }
    }
    
    return true;
  }
  
  return true;
};
